import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Toast } from '@components/_atoms/Toast';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

// constants
import { ButtonColor, ButtonType, TextInputVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { useQueryClient } from 'react-query';

// hooks
import { usePatchUserCompanyTitleFetch } from '@hooks/fetch/useUserFetch';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form``;

const ButtonBoxWrapper = styled.div`
  margin-top: 80px;

  @media ${MOBILE_MODE} {
    margin-top: 246px;
  }
`;

export default function CompanyTitleEditForm() {
  const { t } = useTranslation('user');
  const router = useRouter();
  const queryClient = useQueryClient();

  const [companyTitle, setCompanyTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const disabled = !companyTitle || !!errorMessage;

  const { patchUserCompanyTitleFetchMutate } = usePatchUserCompanyTitleFetch();

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCompanyTitle(value);

    checkValidation(value);
  };

  const checkValidation = (value: string) => {
    let message = '';

    if (!value) message = t('edit.require-error-message');
    setErrorMessage(message);
  };

  const handleFormSubmit = () => {
    patchUserCompanyTitleFetchMutate(
      { company_title: companyTitle },
      {
        onSuccess: () => {
          router.back();
          Toast.success(t('edit.success-message'));
          queryClient.invalidateQueries(['user']);
        },
      },
    );
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <TextInputBox
        title={t('edit.companyTitle-title')}
        textInput={{
          variant: errorMessage ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
          value: companyTitle,
          onChange: handleFormChange,
          placeholder: t('edit.companyTitle-placeholder'),
        }}
        errorMessage={errorMessage}
        isRequired
      />
      <ButtonBoxWrapper>
        <DefaultButtonBox
          button1={
            <Button
              text={t('edit.back-button')}
              type={ButtonType.Button}
              color={ButtonColor.OutlinedBlack}
              onClick={() => router.back()}
              steelbosoId={ButtonCode.BTN_BACK}
            />
          }
          button2={<Button disabled={disabled} text={t('edit.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />}
        />
      </ButtonBoxWrapper>
    </Layout>
  );
}
